import axios from "axios";
import {
  trackAddFilterDropdownClicked,
  trackNewFilterDropdownItemClicked,
  trackNewFilterPopoverSubmitted,
  trackSubmitUpdateFilter,
} from "instrumentation/tracking/page-tracking-events/PastOrdersPageTracking";
import { DateTime } from "luxon";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import IssueRefundsModal from "../../components/owner-app/modals/search-past-orders/IssueRefundsModal";
import {
  BbotButton,
  BbotTable,
  Button,
  Col,
  Input,
  Menu,
  Popover,
  Row,
  Tag,
  Typography,
  notification,
} from "../../top-component-library";
import DateInput from "../../top-component-library/form-inputs/DateInput";
import { formatPrice, generalErrorAlert, generateUUIDv4 } from "../../util/Utils";
import { groupModsByQuantity, getGiftCardCharges, calculateTotalGiftCardAmountAuthorized } from './pastOrdersUtils';

const PAGINATION_DEFAULTS = {
  current: 1,
  pageSize: 20,
};

const SEARCH_ORDER_FILTERS = {
  startDate: { filterName: "Start Date", filterKey: "start_date" },
  endDate: { filterName: "End Date", filterKey: "end_date" },
  last4: { filterName: "Credit / Gift Card Last 4", filterKey: "last4" },
  cardExpMonth: { filterName: "Credit Card Exp Month", filterKey: "exp_month" },
  cardExpYear: { filterName: "Credit Card Exp Year", filterKey: "exp_year" },
  orderNumber: { filterName: "Order Number", filterKey: "order_number" },
  locationCode: { filterName: "Location", filterKey: "locationShortId" },
  phoneNumber: { filterName: "Phone Number", filterKey: "phone_number" },
};

const PastOrdersLookupTab = (props) => {
  const [tableState, setTableState] = useState({
    orders: [],
    paginationData: PAGINATION_DEFAULTS,
    paginationThreshold: null,
    isLoading: false,
    orderQueryPromiseId: null,
  });

  const [filterState, setFilterState] = useState({
    searchOrderFilters: [
      { filterType: "startDate", filterName: "Start Date", filterValue: DateTime.now().minus({ weeks: 1 }) },
      { filterType: "endDate", filterName: "End Date", filterValue: DateTime.now() },
    ],
    orderFilterVisibility: {
      startDate: false, endDate: false, last4: false, cardExpMonth: false,
      cardExpYear: false, orderNumber: false, locationCode: false, phoneNumber: false,
    },
    newFilterType: null,
    showAddFilterPopover: false,
  });

  const [modalState, setModalState] = useState({
    orderBeingRefunded: null,
    orderFees: [],
    currencyData: null,
  });

  const [emailState, setEmailState] = useState({
    emailPopoverOrderIds: [],
    isSendingEmail: false,
  });

  const orderQueryPromiseRef = useRef(); // Must use ref to get most up to date id within the callback. https://stackoverflow.com/questions/57847594/react-hooks-accessing-up-to-date-state-from-within-a-callback

  const { selectedCustomer, stations, isSelectedCustomerFlaggedForDovetail } = props;

  orderQueryPromiseRef.current = tableState.orderQueryPromiseId;

  useEffect(() => {
    if (!selectedCustomer) return;
    searchForOrders(PAGINATION_DEFAULTS);
    setTableState(prev => ({ ...prev, paginationData: PAGINATION_DEFAULTS }));
  }, [filterState.searchOrderFilters, selectedCustomer]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchForOrders = async (paginationInfo = tableState.paginationData) => {
    setTableState(prev => ({ ...prev, isLoading: true }));

    const filtersByType = Object.fromEntries(
      filterState.searchOrderFilters.map((searchOrderFilter) => [
        SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterKey,
        searchOrderFilter.filterType === "startDate" || searchOrderFilter.filterType === "endDate"
          ? searchOrderFilter.filterValue.toUTC().toISO()
          : searchOrderFilter.filterValue,
      ])
    );
    const payload = {
      customer_id: selectedCustomer.customer_id,
      page: paginationInfo?.current,
      page_size: paginationInfo?.pageSize,
      ...filtersByType,
    };

    // set the order query id
    const newOrderQueryId = generateUUIDv4();
    setTableState(prev => ({ ...prev, orderQueryPromiseId: newOrderQueryId }));

    // resolve all promises
    try {
      const res = await axios.get("api/findReceiptsFromOwner", {
        params: payload,
      });

      if (orderQueryPromiseRef.current !== newOrderQueryId) {
        return;
      }

      setTableState(prev => ({
        ...prev,
        orders: res.data.orders,
        paginationData: {
          ...prev.paginationData,
          total: res.data.numOrders,
        },
        paginationThreshold: res.data.paginationThreshold,
      }));
      setModalState(prev => ({
        ...prev,
        orderFees: res.data.orderFees,
        currencyData: res.data.currencyData,
      }));
    } catch (error) {
      generalErrorAlert(error, "Could not look up orders. ", true);
    }
    setTableState(prev => ({ ...prev, isLoading: false }));
  };

  const sendEmailReceipt = async (orderId, email, customerId) => {
    if (email === "") return;

    setEmailState(prev => ({ ...prev, isSendingEmail: true }));
    try {
      const payload = {
        email: email,
        order_id: orderId,
        customer_id: customerId,
      };
      await axios.post("api/emailReceiptFromOwner", payload);
      notification.success({ message: "Sent receipt email to " + email });
      toggleEmailPopoverVisibility(orderId, false);
    } catch (error) {
      generalErrorAlert(error, "Could not send email receipt.");
    }
    setEmailState(prev => ({ ...prev, isSendingEmail: false }));
  };

  const onTableChange = (pagination, filters, sorter) => {
    if (pagination.current && tableState.paginationThreshold && pagination.total > tableState.paginationThreshold) {
      searchForOrders(pagination);
    }
    setTableState(prev => ({ ...prev, paginationData: pagination }));
  };

  const toggleFilterVisibility = (filterType, visible) => {
    setFilterState(prev => ({
      ...prev,
      orderFilterVisibility: {
        ...prev.orderFilterVisibility,
        [filterType]: visible,
      },
    }));
  };

  const removeFilter = (filterType) => {
    setFilterState(prev => ({
      ...prev,
      searchOrderFilters: prev.searchOrderFilters.filter(
        (searchOrderFilter) => searchOrderFilter.filterType !== filterType
      ),
    }));
  };

  const toggleEmailPopoverVisibility = (orderId, visible) => {
    setEmailState(prev => ({
      ...prev,
      emailPopoverOrderIds: visible
        ? [...prev.emailPopoverOrderIds, orderId]
        : prev.emailPopoverOrderIds.filter((id) => id !== orderId),
    }));
  };

  const addFilter = (filterType, filterValue) => {
    setFilterState(prev => ({
      ...prev,
      searchOrderFilters: [
        ...prev.searchOrderFilters,
        { filterType: filterType, filterValue: filterValue },
      ],
    }));
  };

  const updateFilter = (filterType, filterValue) => {
    setFilterState(prev => ({
      ...prev,
      searchOrderFilters: prev.searchOrderFilters.map((searchOrderFilter) =>
        searchOrderFilter.filterType === filterType
          ? { filterType: filterType, filterValue: filterValue }
          : searchOrderFilter
      ),
    }));
  };

  const addFilterContent = () => {
    if (filterState.newFilterType) {
      return (
        <div className={"padding-y-2 padding-x-1"}>
          <StyledSearch
            placeholder={SEARCH_ORDER_FILTERS[filterState.newFilterType].filterName}
            onSearch={(newFilterValue) => {
              if (newFilterValue.length === 0) return;
              addFilter(filterState.newFilterType, newFilterValue);
              setFilterState(prev => ({ ...prev, showAddFilterPopover: false, newFilterType: null }));
              trackNewFilterPopoverSubmitted({ filter_type: filterState.newFilterType, filter_value: newFilterValue });
            }}
            data-test-id={`add-filter-input-${SEARCH_ORDER_FILTERS[filterState.newFilterType].filterKey}`}
            enterButton={
              <StyledSearchButton
                type={"primary"}
                data-test-id={`add-filter-button-${SEARCH_ORDER_FILTERS[filterState.newFilterType].filterKey}`}
                className={"ant-input-search-button"}
              >
                Add
              </StyledSearchButton>
            }
          />
        </div>
      );
    }

    const existingFilterTypes = filterState.searchOrderFilters.map((searchOrderFilter) => searchOrderFilter.filterType);
    const filtersToShow = Object.keys(SEARCH_ORDER_FILTERS).filter(
      (searchOrderFilterType) => !existingFilterTypes.includes(searchOrderFilterType)
    );

    return (
      <Menu
        onClick={(newFilter) => {
          setFilterState(prev => ({ ...prev, newFilterType: newFilter.key }));
          trackNewFilterDropdownItemClicked({ filter_type: newFilter.key });
        }}
      >
        {filtersToShow.map((filterType) => (
          <Menu.Item key={filterType} data-test-id={`new-filter-${SEARCH_ORDER_FILTERS[filterType].filterKey}`}>
            {SEARCH_ORDER_FILTERS[filterType].filterName}
          </Menu.Item>
        ))}
      </Menu>
    );
  };

  const editFilterContent = (searchOrderFilter) => {
    if (searchOrderFilter.filterType === "startDate" || searchOrderFilter.filterType === "endDate")
      return (
        <div className={"padding-y-2 padding-x-1"}>
          <DateInput
            showTime
            defaultValue={moment(searchOrderFilter.filterValue.toISO())}
            onOk={(newDate) => {
              updateFilter(searchOrderFilter.filterType, DateTime.fromISO(newDate.toISOString()));
              toggleFilterVisibility(searchOrderFilter.filterType, false);
              trackSubmitUpdateFilter({
                filter_type: searchOrderFilter.filterType,
                filter_value: newDate.toISOString(),
              });
            }}
          />
        </div>
      );

    return (
      <div className={"padding-y-2 padding-x-1"}>
        <StyledSearch
          defaultValue={searchOrderFilter.filterValue}
          onSearch={(newFilterValue) => {
            if (newFilterValue.length === 0) return;
            updateFilter(searchOrderFilter.filterType, newFilterValue);
            toggleFilterVisibility(searchOrderFilter.filterType, false);
            trackSubmitUpdateFilter({ filter_type: searchOrderFilter.filterType, filter_value: newFilterValue });
          }}
          placeholder={SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterName}
          data-test-id={`edit-filter-input-${SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterKey}`}
          enterButton={
            <StyledSearchButton
              type={"primary"}
              data-test-id={`save-edit-filter-${SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterKey}`}
            >
              Save
            </StyledSearchButton>
          }
        />
      </div>
    );
  };

  const emailReceiptContent = (orderId, customerId) => {
    return (
      <div className={"padding-y-2 padding-x-1"}>
        <StyledSearch
          placeholder={"Guest Email"}
          onSearch={(email) => {
            sendEmailReceipt(orderId, email, customerId);
          }}
          enterButton={"Send"}
          loading={emailState.isSendingEmail}
        />
      </div>
    );
  };

  const orderFilters = () => {
    return (
      <div className={"margin-top-4 margin-bottom-2"}>
        {filterState.searchOrderFilters.map((searchOrderFilter) => {
          const isDateFilter =
            searchOrderFilter.filterType === "startDate" || searchOrderFilter.filterType === "endDate";

          return (
            <Popover
              trigger={"click"}
              placement={"bottom"}
              title={`Update ${SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterName} Filter`}
              content={() => editFilterContent(searchOrderFilter)}
              visible={filterState.orderFilterVisibility[searchOrderFilter.filterType]}
              onVisibleChange={(visible) => toggleFilterVisibility(searchOrderFilter.filterType, visible)}
              overlayInnerStyle={{
                borderRadius: 8,
              }}
              key={`edit-filter-${SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterKey}`}
            >
              <Filter
                className={"cursor-pointer margin-right-2"}
                closable={!isDateFilter}
                onClose={() => removeFilter(searchOrderFilter.filterType)}
                color={"blue"}
                data-test-id={`edit-filter-${SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterKey}`}
              >
                {SEARCH_ORDER_FILTERS[searchOrderFilter.filterType].filterName}:{" "}
                {isDateFilter
                  ? searchOrderFilter.filterValue.toLocaleString(DateTime.DATETIME_SHORT)
                  : searchOrderFilter.filterValue}
              </Filter>
            </Popover>
          );
        })}

        <Popover
          trigger={"click"}
          placement={"bottom"}
          title={filterState.newFilterType ? `Add ${SEARCH_ORDER_FILTERS[filterState.newFilterType].filterName} Filter` : "Add Filter"}
          content={addFilterContent}
          visible={filterState.showAddFilterPopover}
          onVisibleChange={(visible) => {
            setFilterState(prev => ({ ...prev, showAddFilterPopover: visible, newFilterType: null }));
          }}
          overlayInnerStyle={{
            borderRadius: 8,
          }}
        >
          <Filter
            className={"cursor-pointer"}
            data-test-id={"add-filter"}
            onClick={() => trackAddFilterDropdownClicked()}
          >
            + Add Filter
          </Filter>
        </Popover>
      </div>
    );
  };

  const formatCurrency = (amountCents) => {
    return formatPrice(amountCents, modalState.currencyData?.currency, modalState.currencyData?.locale, modalState.currencyData?.centsMultiplier);
  };

  const OrderLineItems = ({ items }) => {
    return items?.map(
      (
        {
          quantity,
          name_for_customer,
          total_pretax_cents,
          total_pretax_cents_before_refunds,
          refund_status,
          mods,
          special_instructions,
        },
        index
      ) => (
        <div key={`${name_for_customer}-${index}`}>
          <Row className={"full-width"}>
            <ItemNameCol span={1}>{quantity}</ItemNameCol>
            <Col span={1}>x</Col>
            <ItemNameCol span={16}>{name_for_customer}</ItemNameCol>

            <ItemPriceCol md={6}>
              {refund_status === "full_refund" ? (
                <span>
                  <Typography.Text delete className={"margin-right-1"}>
                    {formatCurrency(total_pretax_cents_before_refunds)}
                  </Typography.Text>
                  <span>REFUNDED</span>
                </span>
              ) : (
                <span>
                  {total_pretax_cents < total_pretax_cents_before_refunds && (
                    <Typography.Text delete className={"margin-right-1"}>
                      {formatCurrency(total_pretax_cents_before_refunds)}
                    </Typography.Text>
                  )}
                  <span>{formatCurrency(total_pretax_cents)}</span>
                </span>
              )}
            </ItemPriceCol>
          </Row>

          {mods.length > 0 &&
            groupModsByQuantity(mods).map((modifier) => (
              <Row key={modifier.id}>
                <Col span={1}>{modifier.quantity > 1 ? modifier.quantity : ""}</Col>
                <Col span={1}>{modifier.quantity > 1 ? "x" : ""}</Col>
                <Col span={22}>{modifier.name}</Col>
              </Row>
            ))}

          {special_instructions && (
            <Row>
              <Col span={1} />
              <Col span={1} />
              <Col span={22}>{special_instructions}</Col>
            </Row>
          )}

          <hr />
        </div>
      )
    );
  };

  const OrderFeesAndTotals = ({ order, relevantOrderFees, chargesFromGiftCards, totalGiftCardAmountAuthorized, includesRefund }) => {
    return (
      <>
        {relevantOrderFees.map((fee) => (
          <FeeTaxRow key={fee.id}>
            <span>
              {fee.name_for_customer}{" "}
              {fee.status === "refunded" && (
                <PastOrdersLookupTab.AfterRefundLabel>(after refund)</PastOrdersLookupTab.AfterRefundLabel>
              )}
            </span>
            <span>{formatCurrency(fee.status === "refunded" ? 0 : fee.pretax_cents)}</span>
          </FeeTaxRow>
        ))}
        <FeeTaxRow>
          <span>
            Tax{" "}
            {includesRefund && (
              <PastOrdersLookupTab.AfterRefundLabel>(after refund)</PastOrdersLookupTab.AfterRefundLabel>
            )}
          </span>
          <span>
            <span>{formatCurrency(order.tax_cents)}</span>
          </span>
        </FeeTaxRow>
        <FeeTaxRow>
          <span>
            Tip{" "}
            {includesRefund && (
              <PastOrdersLookupTab.AfterRefundLabel>(after refund)</PastOrdersLookupTab.AfterRefundLabel>
            )}
          </span>
          <span>
            <span>{formatCurrency(order.tip_cents)}</span>
          </span>
        </FeeTaxRow>

        <hr />

        {chargesFromGiftCards.map((giftCardCharge) => (
          <FeeTaxRow className={"gift-card-row"} key={giftCardCharge.id}>
            <span>
              {giftCardCharge.giftCardDisplay}{" "}
              {includesRefund && (
                <PastOrdersLookupTab.AfterRefundLabel>(after refund)</PastOrdersLookupTab.AfterRefundLabel>
              )}
            </span>
            <span>
              <span>-{formatCurrency(giftCardCharge.totalAmountForOrder)}</span>
            </span>
          </FeeTaxRow>
        ))}
        <FeeTaxRow className={"total-row"}>
          <span>
            {chargesFromGiftCards.length ? "Total Paid by Customer" : "Order Total"}{" "}
            {includesRefund && (
              <PastOrdersLookupTab.AfterRefundLabel>(after refund)</PastOrdersLookupTab.AfterRefundLabel>
            )}
          </span>
          <span>
            <span>{formatCurrency(Math.max(order.total_cents - totalGiftCardAmountAuthorized, 0))}</span>
          </span>
        </FeeTaxRow>
      </>
    );
  };

  const OrderActions = ({ order }) => {
    return (
      <div className={"margin-y-1"}>
        <Popover
          trigger={"click"}
          placement={"bottom"}
          title={"Send Receipt Email"}
          content={() => emailReceiptContent(order.orderId, order.customerId)}
          visible={emailState.emailPopoverOrderIds.includes(order.orderId)}
          onVisibleChange={(visible) => {
            toggleEmailPopoverVisibility(order.orderId, visible);
          }}
          overlayInnerStyle={{
            borderRadius: 8,
          }}
        >
          <BbotButton
            type={"primary"}
            className={"margin-right-2"}
            icon={<span className={"material-icons-outlined"}>email</span>}
          >
            Email Receipt
          </BbotButton>
        </Popover>
        {/* If customer is flagged for dovetail, disabled the refunds button */}
        <BbotButton onClick={() => setModalState(prev => ({ ...prev, orderBeingRefunded: order }))} disabled={isSelectedCustomerFlaggedForDovetail}>
          Issue Refunds
        </BbotButton>
      </div>
    );
  };

  const orderDetails = (order) => {
    const relevantOrderFees = modalState.orderFees.filter((fee) => fee.primary_order === order.orderId);
    const includesFullRefundInOrder = order.line_item_summaries.every(
      (summary) => summary.refund_status === "full_refund"
    );
    const includesPartialRefundInOrder = order.line_item_summaries.some(
      (summary) => summary.refund_status === "partial_refund"
    );
    const chargesFromGiftCards = getGiftCardCharges(order);
    const totalGiftCardAmountAuthorized = calculateTotalGiftCardAmountAuthorized(chargesFromGiftCards);
    const includesRefund = includesFullRefundInOrder || includesPartialRefundInOrder;

    return (
      <div>
        <OrderDetailsContainer>
          <OrderLineItems items={order.line_item_summaries} />
          <OrderFeesAndTotals
            order={order}
            relevantOrderFees={relevantOrderFees}
            chargesFromGiftCards={chargesFromGiftCards}
            totalGiftCardAmountAuthorized={totalGiftCardAmountAuthorized}
            includesRefund={includesRefund}
          />
          <OrderActions order={order} />
        </OrderDetailsContainer>
      </div>
    );
  };

  const ordersTable = () => {
    return (
      <BbotTable
        id={"orders-table"}
        interactive={false}
        columns={["Order Number", "Account Name", "Date", "Location", "Last 4", "Total Charge", "Total Items"]}
        data={tableState.orders.map((order) => ({
          key: order.orderId,
          order_number: (
            <span>
              <PastOrdersLookupTab.OrderNumberWithPrefix>
                {order.orderNumberWithPrefix}
              </PastOrdersLookupTab.OrderNumberWithPrefix>
              {order.order_refund_status === "fully_refunded" && (
                <PastOrdersLookupTab.RefundLabel data-type="full_refund">
                  Fully Refunded
                </PastOrdersLookupTab.RefundLabel>
              )}
              {order.order_refund_status === "partially_refunded" && (
                <PastOrdersLookupTab.RefundLabel data-type="partial_refund">
                  Partially Refunded
                </PastOrdersLookupTab.RefundLabel>
              )}
            </span>
          ),
          account_name: order.customerName,
          date: DateTime.fromISO(order.time).toLocaleString(DateTime.DATETIME_SHORT),
          location: order.locationShortCode,
          last_4:
            order.charges.find((charge) => !!charge.card_info?.last4)?.card_info.last4 ||
            order.charges.find((charge) => !!charge.gift_card_last4)?.gift_card_last4,
          total_charge: formatPrice(
            order.total_cents,
            modalState.currencyData?.currency,
            modalState.currencyData?.locale,
            modalState.currencyData?.centsMultiplier
          ),
          total_items: order.items.length,
          orderInfo: order,
        }))}
        pagination={tableState.paginationData}
        onChange={onTableChange}
        loading={tableState.isLoading}
        expandable={{
          expandedRowRender: (order) => orderDetails(order.orderInfo),
        }}
      />
    );
  };

  return (
    <div>
      {orderFilters()}
      {ordersTable()}

      {/* If customer is flagged for dovetail, do not allow refunds to be issued */}
      {!isSelectedCustomerFlaggedForDovetail && (
        <IssueRefundsModal
          onCancelCallback={() => setModalState(prev => ({ ...prev, orderBeingRefunded: null }))}
          onSuccessCallback={searchForOrders}
          order={modalState.orderBeingRefunded}
          orderFees={modalState.orderFees}
          currencyData={modalState.currencyData}
          groupModsByQuantity={groupModsByQuantity}
          stations={stations}
        />
      )}
    </div>
  );
};

export default PastOrdersLookupTab;

const Filter = styled(Tag)`
  display: inline-flex;
  align-items: center;
  padding: 8px;
  font-size: 1rem;
  border-radius: 8px;
`;

const StyledSearch = styled(Input.Search)``;

const StyledSearchButton = styled(Button)`
  background-color: var(--color-primary__regular);
`;

const OrderDetailsContainer = styled.div`
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 20px;
  padding-bottom: 20px;
`;

const FeeTaxRow = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  &.total-row {
    font-weight: 700;
    margin-bottom: 16px;
  }
`;

const ItemPriceCol = styled(Col)`
  text-align: right;
`;

const ItemNameCol = styled(Col)`
  font-weight: 700;
  font-size: 14px;
`;

PastOrdersLookupTab.AfterRefundLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  font-style: italic;
`;

PastOrdersLookupTab.OrderNumberWithPrefix = styled.span``;

PastOrdersLookupTab.RefundLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  font-style: italic;
  background-color: #ccc;
  border-radius: 100px;
  padding: 0.25rem 0.5rem;
  margin-left: 1rem;

  &[data-type="full_refund"] {
    color: var(--color-success__dark);
    background-color: var(--color-success__light);
  }

  &[data-type="partial_refund"] {
    color: var(--color-warning__dark);
    background-color: var(--color-warning__light);
  }
`;
